import React, { useState, useEffect, useCallback } from 'react'
import { useStateValue } from '@State'
import { client } from '@Utils'
import { gradebook as endpoints } from '@URLS'
import Datetime from 'react-datetime'

import { LoadingSpinner } from '@CommonComponents'
import { fetchGradebookData } from './Gradebook'
import { FilterFormLabel } from './FilterFormLabel'
import { FilterFormSections } from './FilterFormSections'

const UNASSIGNED_SECTION_KEY = 'unassigned_section_key'
const DATE_FORMAT = 'YYYY-MM-DD HH:MM'

export const FilterForm = () => {
  const [currentState, dispatch] = useStateValue()
  const [loading, setLoading] = useState(false)
  const [lastName, setLastName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [signupDateStart, setSignupDateStart] = useState('')
  const [signupDateEnd, setSignupDateEnd] = useState('')
  const [showClearButton, setShowClearButton] = useState(false)

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const response = await client.get(endpoints.sections())
        dispatch({
          type: 'UPDATE_SECTIONS',
          payload: response.data.sections,
        })
      } catch (error) {
        reportError('Getting the section data failed.', error)
      } finally {
        setLoading(false)
      }
    }

    fetchSections()
  }, [])

  const updateData = useCallback(
    (data, showClearButton) => {
      setShowClearButton(showClearButton) // always set

      dispatch({
        type: 'SET_LOADING',
        payload: true,
      })
      dispatch({
        type: 'UPDATE_FILTERS',
        payload: data,
      })
      fetchGradebookData(data, dispatch)
    },
    [dispatch, fetchGradebookData]
  )

  const applyFilters = useCallback(
    (showClearButton = true) => {
      const data = {
        first_name: firstName,
        last_name: lastName,
        limit: currentState.limit,
      }

      if (signupDateStart) {
        data.signup_date_start = signupDateStart.format(DATE_FORMAT)
      }
      if (signupDateEnd) {
        data.signup_date_end = signupDateEnd.format(DATE_FORMAT)
      }

      let allSectionsSelected = currentState.sections.every(s =>
        currentState.selectedSections.includes(s)
      )
      if (!allSectionsSelected && currentState.selectedSections.length > 0) {
        data.sections = currentState.selectedSections.join(',')
      }

      updateData(data, showClearButton)
    },
    [
      firstName,
      lastName,
      signupDateStart,
      signupDateEnd,
      currentState,
      updateData,
    ]
  )

  const clearFilters = useCallback(() => {
    setLastName('')
    setFirstName('')
    setSignupDateStart('')
    setSignupDateEnd('')
    dispatch({
      type: 'UPDATE_SELECTED_SECTIONS',
      payload: [],
    })
    const resetData = {
      first_name: '',
      last_name: '',
      limit: currentState.limit,
      sections: [],
    }

    updateData(resetData, false)
  }, [dispatch, currentState.limit, updateData])

  const handleFormSubmit = e => {
    e.preventDefault()
    applyFilters()
  }

  const handleAllSelectionClick = selectAll => {
    let selectedSections
    if (selectAll) {
      selectedSections = [UNASSIGNED_SECTION_KEY, ...currentState.sections]
    } else {
      selectedSections = []
    }
    dispatch({
      type: 'UPDATE_SELECTED_SECTIONS',
      payload: selectedSections,
    })
  }

  return loading ? (
    <span className="form-container form-container-vertical">
      <div className="main spinner-holder">
        <LoadingSpinner style={{ fontSize: '50px' }} />
      </div>
    </span>
  ) : (
    <span className="form-container form-container-vertical">
      <form className="custom-form" onSubmit={handleFormSubmit} id="form">
        <div className="form-response-message" />
        <div className="form-vertical form-fieldset-container">
          <FilterFormLabel htmlFor="lastName" labelText="Last Name Contains:">
            <input
              className="form-control"
              name="lastName"
              id="lastName"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              type="text"
            />
          </FilterFormLabel>

          <FilterFormLabel htmlFor="firstName" labelText="First Name Contains:">
            <input
              className="form-control"
              id="firstName"
              name="firstName"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              type="text"
            />
          </FilterFormLabel>

          <div className="signupDate align-items-base d-flex justify-content-start flex-column flex-xl-row">
            <FilterFormLabel
              htmlFor="signup-date-from"
              labelText="Signup Date Start"
            >
              <Datetime
                inputProps={{
                  className: 'form-control',
                  id: 'signup-date-from',
                  name: 'signupDateStart',
                  type: 'text',
                }}
                onChange={val => setSignupDateStart(val)}
                value={signupDateStart}
                closeOnSelect
              />
            </FilterFormLabel>
            <div className="left-pad" />
            <FilterFormLabel
              htmlFor="signup-date-to"
              labelText="Signup Date End"
            >
              <Datetime
                inputProps={{
                  className: 'form-control',
                  id: 'signup-date-to',
                  name: 'signupDateEnd',
                  type: 'text',
                }}
                onChange={val => setSignupDateEnd(val)}
                value={signupDateEnd}
                closeOnSelect
              />
            </FilterFormLabel>
          </div>

          <FilterFormLabel
            id="multi-select-course-sections"
            htmlFor="course-sections"
            labelText="Course Section:"
          >
            <a onClick={() => handleAllSelectionClick(true)} className="mr-4">
              Select All
            </a>
            <a onClick={() => handleAllSelectionClick(false)}>Unselect All</a>
            <FilterFormSections unassignedKey={UNASSIGNED_SECTION_KEY} />
          </FilterFormLabel>

          <div className="form-div-submit form-submit-buttons justify-content-around d-flex">
            <input
              id="submit_button_form"
              name="submit_button"
              type="hidden"
              value=""
            />
            <button
              className="btn custom-form-submit-button btn-default btn-myed btn-default-myed"
              data-form-container-id="g15295527c81a2001x2"
              id="submit_button_form_0"
              tabIndex="0"
              type="submit"
            >
              Apply Filters
            </button>
            {showClearButton && (
              <button
                id="clear-filters"
                className="btn btn-myed btn-default btn-default-myed"
                onClick={clearFilters}
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </form>
    </span>
  )
}
